// ===================================
// KAIXO - Receptor de share externo
// Cuando otra app comparte algo a KAIXO (foto/video/texto),
// este modal se abre con la lista de contactos/grupos para reenviar.
// ===================================

import React, { useState, useEffect, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { Icons } from '../Icons/Icons';
import {
    getConversations, getUserGroups, getAllUsers,
    sendMessage, sendGroupMessage,
    sendImage, sendGroupImage,
    sendVideo, sendGroupVideo
} from '../../services/api';
import './ShareReceiverModal.css';

// Lee el archivo compartido como Blob.
// Confirmado por logcat: send-intent v7 en Android entrega URIs file://
// (ej: file:///data/user/0/com.kaixo.app/files/foto.jpg) y los WebView
// no pueden hacer fetch() de file:// por seguridad.
// Usamos Capacitor.convertFileSrc que convierte file:// a un esquema
// https://localhost/_capacitor_file_/... que el WebView SÍ puede fetch.
// Si eso falla, fallback a Filesystem.readFile (lee binario y lo
// transformamos a Blob).
async function readSharedAsBlob(url, mimeHint) {
    const isNative = !!(window.Capacitor && window.Capacitor.isNativePlatform && window.Capacitor.isNativePlatform());

    // URLs http(s)/blob/data: fetch directo
    if (/^(https?:|blob:|data:)/i.test(url)) {
        const r = await fetch(url);
        if (!r.ok) throw new Error(`fetch falló (${r.status})`);
        return await r.blob();
    }

    if (!isNative) {
        // Web: intenta fetch directo (puede que sea blob: o similar)
        const r = await fetch(url);
        if (!r.ok) throw new Error(`fetch falló (${r.status})`);
        return await r.blob();
    }

    // Nativo + URI file://content://: 1) convertFileSrc + fetch
    try {
        const safeUrl = window.Capacitor.convertFileSrc ? window.Capacitor.convertFileSrc(url) : url;
        const r = await fetch(safeUrl);
        if (r.ok) {
            const blob = await r.blob();
            if (blob && blob.size > 0) return blob;
        }
    } catch (e) {
        // sigue al fallback
    }

    // 2) Fallback: Filesystem.readFile (base64 → Blob)
    const { Filesystem } = await import('@capacitor/filesystem');
    const result = await Filesystem.readFile({ path: url });
    const data = result?.data;
    if (!data) throw new Error('Filesystem.readFile devolvió vacío');
    if (data instanceof Blob) return data;
    if (typeof data === 'string') {
        const byteCharacters = atob(data);
        const byteArray = new Uint8Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
            byteArray[i] = byteCharacters.charCodeAt(i);
        }
        return new Blob([byteArray], { type: mimeHint || 'application/octet-stream' });
    }
    throw new Error('Formato de Filesystem.readFile desconocido');
}

/**
 * Props:
 *   payload: { type, url, title, description }
 *   currentUser
 *   onClose
 *   onComplete: callback cuando termina el envío
 */
function ShareReceiverModal({ payload, currentUser, onClose, onComplete }) {
    const [conversations, setConversations] = useState([]);
    const [groups, setGroups] = useState([]);
    const [users, setUsers] = useState([]);
    const [selected, setSelected] = useState(new Set());
    const [query, setQuery] = useState('');
    const [loading, setLoading] = useState(true);
    const [sending, setSending] = useState(false);
    const [mediaBlob, setMediaBlob] = useState(null);
    const [previewUrl, setPreviewUrl] = useState('');
    const [error, setError] = useState('');

    const isImage = payload?.type?.startsWith('image/');
    const isVideo = payload?.type?.startsWith('video/');
    const isText = payload?.type === 'text/plain';
    const sharedText = payload?.description || payload?.title || '';

    // Diagnóstico del ciclo de vida del modal — para detectar si Android
    // mata la activity antes de que el usuario interactúe.
    useEffect(() => {
        try { window.kaixoDebugLog && window.kaixoDebugLog('share.modal.MOUNTED', { ts: Date.now() }); } catch {}
        return () => {
            try { window.kaixoDebugLog && window.kaixoDebugLog('share.modal.UNMOUNTED', { ts: Date.now() }); } catch {}
            // NO llamar SendIntent.finish() aquí: si la app fue cold-launched
            // por el share, finish() le dice a Android "ya cumplí mi intent"
            // y Android cierra la activity, sacando KAIXO del foreground. El
            // usuario tendría que reabrir la app desde el menú. En su lugar
            // dejamos que Android limpie el intent solo cuando la app se
            // cierre normalmente. El re-check evita reabrir el modal porque
            // App.js lleva control de qué payload ya fue procesado vía la
            // dependencia [currentUser?._id] del useEffect.
        };
    }, []);

    useEffect(() => {
        let cancelled = false;
        const loadAll = async () => {
            try {
                const [convRes, groupRes, usersRes] = await Promise.all([
                    getConversations(currentUser._id),
                    getUserGroups(currentUser._id),
                    getAllUsers()
                ]);
                if (cancelled) return;
                setConversations(convRes.data.conversations || []);
                setGroups(groupRes.data.groups || []);
                setUsers((usersRes.data.users || []).filter(u =>
                    u._id !== currentUser._id && !u.phone?.startsWith('widget_')
                ));
            } catch (e) {
                setError('No se pudieron cargar los contactos');
            } finally {
                if (!cancelled) setLoading(false);
            }
        };
        loadAll();
        return () => { cancelled = true; };
    }, [currentUser._id]);

    // Cargar el blob del archivo compartido (vía readSharedAsBlob que
    // maneja file://, content:// y http(s)).
    useEffect(() => {
        let cancelled = false;
        let blobUrl = null;
        const log = (k, v) => { try { window.kaixoDebugLog && window.kaixoDebugLog(k, v); } catch {} };
        const loadBlob = async () => {
            log('share.modal.payload', { type: payload?.type, urlPrefix: (payload?.url || '').slice(0, 60), hasUrl: !!payload?.url, isImage, isVideo });
            if (!payload?.url || (!isImage && !isVideo)) {
                log('share.modal.loadBlob.skipped', { reason: !payload?.url ? 'no-url' : 'not-media' });
                return;
            }
            const mimeHint = payload.type || (isImage ? 'image/jpeg' : 'video/mp4');
            try {
                log('share.modal.read.start', payload.url.slice(0, 80));
                const blob = await readSharedAsBlob(payload.url, mimeHint);
                log('share.modal.blob.created', { size: blob.size, type: blob.type });
                if (cancelled) return;
                if (!blob || blob.size === 0) throw new Error('blob vacío');
                setMediaBlob(blob);
                blobUrl = URL.createObjectURL(blob);
                setPreviewUrl(blobUrl);
            } catch (e) {
                log('share.modal.read.error', { name: e?.name, message: e?.message, stack: (e?.stack || '').slice(0, 200) });
                console.error('[Share] read error:', e);
                if (!cancelled) setError(`No se pudo cargar el archivo: ${e?.message || 'error'}`);
            }
        };
        loadBlob();
        return () => {
            cancelled = true;
            if (blobUrl) URL.revokeObjectURL(blobUrl);
        };
    }, [payload, isImage, isVideo]);

    // Lista combinada de targets (grupos + contactos + usuarios)
    const targets = useMemo(() => {
        const out = [];
        groups.forEach(g => out.push({
            key: `g-${g._id}`,
            id: g._id,
            type: 'group',
            name: g.name,
            avatar: g.avatar,
            subtitle: `${g.members?.length || 0} miembros`
        }));
        conversations.forEach(c => out.push({
            key: `c-${c.id}`,
            id: c.id,
            receiverId: c.otherUser?.id,
            type: 'conversation',
            name: c.otherUser?.name || '',
            avatar: c.otherUser?.avatar,
            subtitle: c.otherUser?.language || ''
        }));
        // Usuarios que no estén ya en conversaciones
        const existingReceivers = new Set(conversations.map(c => c.otherUser?.id).filter(Boolean));
        users.forEach(u => {
            if (existingReceivers.has(u._id)) return;
            out.push({
                key: `u-${u._id}`,
                id: null,
                receiverId: u._id,
                type: 'user',
                name: u.name,
                avatar: u.avatar,
                subtitle: u.language || ''
            });
        });
        if (!query.trim()) return out;
        const q = query.toLowerCase();
        return out.filter(t => t.name.toLowerCase().includes(q));
    }, [groups, conversations, users, query]);

    const toggle = (key) => {
        setSelected(prev => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });
    };

    const handleSend = async () => {
        if (selected.size === 0) return;
        const log = (k, v) => { try { window.kaixoDebugLog && window.kaixoDebugLog(k, v); } catch {} };
        log('share.send.start', { count: selected.size, hasMediaBlob: !!mediaBlob, blobSize: mediaBlob?.size, blobType: mediaBlob?.type });
        setSending(true);
        setError('');
        const selectedTargets = targets.filter(t => selected.has(t.key));
        try {
            for (const t of selectedTargets) {
                log('share.send.target', { type: t.type, name: t.name });
                if (isImage && mediaBlob) {
                    const fd = new FormData();
                    const filename = `shared-${Date.now()}.${(payload.type.split('/')[1] || 'jpg')}`;
                    fd.append('image', mediaBlob, filename);
                    fd.append('senderId', currentUser._id);
                    fd.append('caption', sharedText || '');
                    if (t.type === 'group') { fd.append('groupId', t.id); await sendGroupImage(fd); }
                    else { fd.append('receiverId', t.receiverId); await sendImage(fd); }
                } else if (isVideo && mediaBlob) {
                    const fd = new FormData();
                    const filename = `shared-${Date.now()}.${(payload.type.split('/')[1] || 'mp4')}`;
                    fd.append('video', mediaBlob, filename);
                    fd.append('senderId', currentUser._id);
                    fd.append('caption', sharedText || '');
                    if (t.type === 'group') { fd.append('groupId', t.id); await sendGroupVideo(fd); }
                    else { fd.append('receiverId', t.receiverId); await sendVideo(fd); }
                } else if (isText || sharedText) {
                    const text = sharedText;
                    if (!text) continue;
                    if (t.type === 'group') {
                        await sendGroupMessage({ groupId: t.id, senderId: currentUser._id, text });
                    } else {
                        await sendMessage({ senderId: currentUser._id, receiverId: t.receiverId, text });
                    }
                }
            }
            log('share.send.success', { count: selectedTargets.length });
            if (onComplete) onComplete();
            onClose();
        } catch (e) {
            log('share.send.error', {
                name: e?.name,
                message: e?.message,
                status: e?.response?.status,
                data: e?.response?.data,
                stack: (e?.stack || '').slice(0, 300)
            });
            console.error('[Share] send error:', e);
            setError(e?.response?.data?.error || e?.message || 'Error al enviar');
        } finally {
            setSending(false);
        }
    };

    return ReactDOM.createPortal(
        <div className="shr-overlay" onClick={onClose}>
            <div className="shr-modal" onClick={e => e.stopPropagation()}>
                <div className="shr-header">
                    <h3>Enviar a...</h3>
                    <button className="shr-close" onClick={onClose} aria-label="Cerrar">
                        <Icons.Close size={18} />
                    </button>
                </div>

                <div className="shr-preview">
                    {isImage && previewUrl && (
                        <img src={previewUrl} alt="" className="shr-preview-media" />
                    )}
                    {isVideo && previewUrl && (
                        <video src={previewUrl} className="shr-preview-media" controls />
                    )}
                    {isText && sharedText && (
                        <div className="shr-preview-text">{sharedText}</div>
                    )}
                </div>

                <div className="shr-search">
                    <Icons.Search size={15} />
                    <input
                        value={query}
                        onChange={e => setQuery(e.target.value)}
                        placeholder="Buscar..."
                    />
                </div>

                <div className="shr-body">
                    {loading && <p className="shr-center">Cargando...</p>}
                    {!loading && targets.length === 0 && (
                        <p className="shr-center">No hay contactos disponibles.</p>
                    )}
                    {!loading && targets.map(t => (
                        <label key={t.key} className="shr-target">
                            <input
                                type="checkbox"
                                checked={selected.has(t.key)}
                                onChange={() => toggle(t.key)}
                            />
                            <div className={`shr-avatar ${t.type === 'group' ? 'group' : ''}`}>
                                {t.avatar
                                    ? <img src={t.avatar} alt="" />
                                    : (t.name?.[0] || '?').toUpperCase()}
                            </div>
                            <div className="shr-meta">
                                <div className="shr-name">
                                    {t.type === 'group' && <Icons.Group size={13} />}
                                    {t.name}
                                </div>
                                {t.subtitle && <div className="shr-subtitle">{t.subtitle}</div>}
                            </div>
                        </label>
                    ))}
                </div>

                {error && <div className="shr-error">{error}</div>}

                {selected.size > 0 && (
                    <div className="shr-footer">
                        <button className="shr-send" onClick={handleSend} disabled={sending}>
                            {sending
                                ? 'Enviando...'
                                : (
                                    <>
                                        <Icons.Send size={16} color="white" />
                                        {' '}Enviar a {selected.size} {selected.size === 1 ? 'chat' : 'chats'}
                                    </>
                                )}
                        </button>
                    </div>
                )}
            </div>
        </div>,
        document.body
    );
}

export default ShareReceiverModal;
